/**
 * AcessoDiretoFlow.tsx — Fluxo simplificado de ativação (Acesso Direto).
 * Usado quando acessoDireto=true no convite.
 // ──── Google Sign-In ──── 
 */
import { useEffect, useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Shield, CheckCircle2, Loader2, Mail, Fingerprint, KeyRound, Pencil, Check, Camera, LogOut, MapPin } from 'lucide-react';
import { startRegistration } from '@simplewebauthn/browser';
import { useAuthStore } from '../../store/authStore';
import { markPasskeyOnThisDevice } from '../../auth/passkeyDevice';
import { getRedirectPath } from '../../auth/routes';
import { saveAccountToLocal } from '../../auth/savedAccounts';
import { trackEvent } from '../../api/observe';
import { AcShieldLogo } from '../../components/security/AcShieldLogo';
import { SovereignSplash } from '../../components/security/SovereignSplash';
import {
  requestPasskeyRegistrationOptions,
  verifyPasskeyRegistration,
} from '../../security/passkey/passkeyApi';

const API = import.meta.env.VITE_API_URL || '/api/v1';

// ──── Sovereign Dark Styles ──── 
const SOVEREIGN_BG: React.CSSProperties = {
  width: '100%',
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  fontFamily: 'Inter, system-ui, -apple-system, sans-serif',
};


const SOVEREIGN_CARD: React.CSSProperties = {
  background: 'rgba(255,255,255,0.07)', borderRadius: 28, padding: 'clamp(28px, 5vw, 42px)',
  maxWidth: 480, width: '100%', position: 'relative' as any, zIndex: 1,
  boxShadow: '0 30px 70px rgba(0,0,0,0.35), 0 0 0 1px rgba(255,255,255,0.09)',
  backdropFilter: 'blur(28px)', WebkitBackdropFilter: 'blur(28px)',
  textAlign: 'center' as any,
  border: '1px solid rgba(255,255,255,0.11)',
};

/** Gera iniciais a partir do nome (ex: "Claido Diretor" -> "CD") */
function getInitials(nome: string): string {
  return nome.split(' ').filter(Boolean).slice(0, 2).map(w => w[0]).join('').toUpperCase();
}

type DirectStep =
  | 'choose'
  | 'email-code'
  | 'set-password'
  | 'passkey'
  | 'done';

interface Props {
  token: string;
  autoEmailCode?: string;
  userInfo: { id: string; nome: string; email: string; perfil: string; cargo?: string | null; escola?: { nome: string } | null };
}

export function AcessoDiretoFlow({ token, userInfo, autoEmailCode }: Props) {
  const navigate = useNavigate();
  const [directStep, setDirectStep] = useState<DirectStep>('choose');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleLogout = () => { navigate('/'); };

  // Email code
  const [code, setCode] = useState('');
  const [emailMasked, setEmailMasked] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');

  // Google avatar
  const [googlePhoto, setGooglePhoto] = useState<string | null>(null);

  // JWT temp
  const jwtRef = useRef('');

  const [authMethod, setAuthMethod] = useState<'google' | 'email' | null>(null);

  // ──── Edição de nome ──── 
  const [editingName, setEditingName] = useState(false);
  const [editedName, setEditedName] = useState(userInfo.nome);
  const [savingName, setSavingName] = useState(false);

  const handleSaveName = async () => {
    const trimmed = editedName.trim();
    if (!trimmed || trimmed === userInfo.nome) { setEditingName(false); return; }
    const jwt = jwtRef.current;
    if (!jwt) { setEditingName(false); return; }
    setSavingName(true);
    try {
      const res = await fetch(`${API}/auth/me`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${jwt}` },
        body: JSON.stringify({ nome: trimmed }),
      });
      if (res.ok) {
        userInfo.nome = trimmed; // atualiza localmente
      }
    } catch { /* silencioso */ }
    finally { setSavingName(false); setEditingName(false); }
  };

  // ──── Google Sign-In ──── 
  const handleGoogle = async () => {
    setLoading(true); setError('');
    try {
      const { initializeApp, getApps } = await import('firebase/app');
      const firebaseAuth = await import('firebase/auth');
      const cfg = {
        apiKey: import.meta.env.VITE_FIREBASE_API_KEY,
        authDomain: import.meta.env.VITE_FIREBASE_AUTH_DOMAIN,
        projectId: import.meta.env.VITE_FIREBASE_PROJECT_ID,
        storageBucket: import.meta.env.VITE_FIREBASE_STORAGE_BUCKET,
        messagingSenderId: import.meta.env.VITE_FIREBASE_MESSAGING_SENDER_ID,
        appId: import.meta.env.VITE_FIREBASE_APP_ID,
      };
      const app = getApps().length ? getApps()[0] : initializeApp(cfg);
      const auth = firebaseAuth.getAuth(app);
      const provider = new firebaseAuth.GoogleAuthProvider();
      provider.setCustomParameters({ prompt: 'select_account' });
      const result = await firebaseAuth.signInWithPopup(auth, provider);
      const firebaseToken = await result.user.getIdToken();

      // Salvar foto do Google
      if (result.user.photoURL) setGooglePhoto(result.user.photoURL);

      // Login via Google no backend (gera JWT)
      const res = await fetch(`${API}/auth/login-google`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ firebaseToken }),
      });
      if (!res.ok) throw new Error('Falha no login Google');
      const data = await res.json();
      jwtRef.current = data.access_token;
      trackEvent({ source: 'link_ativacao', action: 'google_auth_completed', targetId: userInfo.id, targetType: 'usuario' });
      setAuthMethod('google');
      setDirectStep('passkey');
    } catch (e: any) {
      if (e.code === 'auth/popup-closed-by-user') { setLoading(false); return; }
      setError(e.message || 'Erro no Google Sign-In');
    } finally { setLoading(false); }
  };

  // ──── Email Code ──── 
  const handleSendCode = async () => {
    setLoading(true); setError('');
    try {
      const res = await fetch(`${API}/auth/send-email-code`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.message || 'Erro ao enviar codigo');
      setEmailMasked(data.emailMasked || userInfo.email);
      setDirectStep('email-code');
    } catch (e: any) { setError(e.message); }
    finally { setLoading(false); }
  };

  const handleVerifyCode = async () => {
    if (code.length !== 6) { setError('Digite o codigo de 6 digitos'); return; }
    setLoading(true); setError('');
    try {
      const res = await fetch(`${API}/auth/verify-email-code`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token, code }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.message || 'Codigo invalido');
      jwtRef.current = data.access_token;
      trackEvent({ source: 'link_ativacao', action: 'email_auth_completed', targetId: userInfo.id, targetType: 'usuario' });
      setAuthMethod('email');
      setDirectStep('set-password');
    } catch (e: any) { setError(e.message); }
    finally { setLoading(false); }
  };

  useEffect(() => {
    if (!autoEmailCode || autoEmailCode.length !== 6) return;
    if (authMethod || jwtRef.current) return;
    setCode(autoEmailCode);
    setDirectStep('email-code');
    setLoading(true);
    setError('');
    fetch(`${API}/auth/verify-email-code`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ token, code: autoEmailCode }),
    })
      .then(async (res) => {
        const data = await res.json().catch(() => ({}));
        if (!res.ok) throw new Error(data.message || 'Código inválido');
        jwtRef.current = data.access_token;
        setAuthMethod('email');
        setDirectStep('set-password');
        trackEvent({
          source: 'link_ativacao',
          action: 'email_auth_completed',
          targetId: userInfo.id,
          targetType: 'usuario',
        });
      })
      .catch((e: any) => {
        setError(e.message || 'Não foi possível validar o código do link.');
      })
      .finally(() => setLoading(false));
  }, [autoEmailCode, authMethod, token, userInfo.id]);

  // ──── Definição de senha após identidade (Google/email) ──── 
  const handleSetPassword = async () => {
    if (newPassword.length < 8) {
      setError('A senha deve ter no mínimo 8 caracteres.');
      return;
    }
    if (newPassword !== confirmPassword) {
      setError('As senhas não conferem.');
      return;
    }
    setLoading(true);
    setError('');
    try {
      const jwt = jwtRef.current;
      const res = await fetch(`${API}/auth/me/password`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${jwt}` },
        body: JSON.stringify({ senha: newPassword }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.message || 'Falha ao cadastrar senha.');

      setNewPassword('');
      setConfirmPassword('');
      setDirectStep('passkey');
      trackEvent({ source: 'link_ativacao', action: 'password_defined', targetId: userInfo.id, targetType: 'usuario' });
    } catch (e: any) {
      setError(e.message || 'Erro ao salvar senha.');
    } finally {
      setLoading(false);
    }
  };

  // ──── Passkey Registration ──── 
  const handlePasskey = async () => {
    setLoading(true); setError('');
    try {
      const jwt = jwtRef.current;
      const requestOptions = { bearerToken: jwt };
      const optData = await requestPasskeyRegistrationOptions(undefined, requestOptions);

      // 2. Browser WebAuthn registration — backend retorna { options: { ... } }
      const regOptions = (optData as any).options || optData;
      const attestation = await startRegistration({ optionsJSON: regOptions as any });

      const verifyResult = await verifyPasskeyRegistration(attestation, undefined, requestOptions);
      if (verifyResult.ok === false) throw new Error('Falha ao registrar Passkey');

      const emailForDevice = (userInfo.email || '').trim().toLowerCase();
      if (emailForDevice) {
        markPasskeyOnThisDevice(emailForDevice);
      }

      // 4. Activate account
      const actRes = await fetch(`${API}/auth/activate-direct`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${jwt}` },
      });
      if (!actRes.ok) throw new Error('Erro ao ativar conta');

      setDirectStep('done');
      setTimeout(goToDashboard, 1200);

      // Observe: passkey registrado
      trackEvent({ source: 'link_ativacao', action: 'passkey_registered', targetId: userInfo.id, targetType: 'usuario' });

      // Login (salvar JWT no store)
      const login = useAuthStore.getState().login;
      const userData = {
        id: userInfo.id,
        nome: userInfo.nome,
        email: userInfo.email,
        perfil: userInfo.perfil as any,
        roles: [userInfo.perfil],
        role: userInfo.perfil,
        cargo: userInfo.cargo || undefined,
      };
      login(jwt, userData as any);
      saveAccountToLocal(userData, 'email');

    } catch (e: any) {
      if (e.name === 'NotAllowedError') { setLoading(false); return; }
      setError(e.message || 'Erro no Passkey');
    } finally { setLoading(false); }
  };

  // ──── Skip Passkey (backup) ──── 
  const handleSkipPasskey = async () => {
    setLoading(true); setError('');
    try {
      const jwt = jwtRef.current;
      const actRes = await fetch(`${API}/auth/activate-direct`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${jwt}` },
      });
      if (!actRes.ok) throw new Error('Erro ao ativar conta');
      setDirectStep('done');
      setTimeout(goToDashboard, 1200);
      const login = useAuthStore.getState().login;
      const userData = {
        id: userInfo.id, nome: userInfo.nome, email: userInfo.email,
        perfil: userInfo.perfil as any, roles: [userInfo.perfil], role: userInfo.perfil,
        cargo: userInfo.cargo || undefined,
      };
      login(jwt, userData as any);
      
      // Save for "Quem está acessando"
      saveAccountToLocal(userData);

    } catch (e: any) {
      if (e.name === 'NotAllowedError') { setLoading(false); return; }
      setError(e.message || 'Erro no Passkey');
    } finally { setLoading(false); }
  };

  //  Redirect final 
  const goToDashboard = () => {
    const path = getRedirectPath(userInfo.perfil, userInfo.cargo || undefined);
    trackEvent({ source: 'link_ativacao', action: 'dashboard_entered', targetId: userInfo.id, targetType: 'usuario' });
    navigate(path, { replace: true });
  };

  //  Styles 
  const btn: React.CSSProperties = {
    flex: 1, padding: '14px 12px', borderRadius: 14, fontSize: '.88rem',
    fontWeight: 700, border: 'none', cursor: 'pointer', display: 'flex',
    alignItems: 'center', justifyContent: 'center', gap: 8, transition: 'all .2s',
  };

  //  Email inline mode 
  const [emailMode, setEmailMode] = useState(false);
  const [editingEmail, setEditingEmail] = useState(false);
  const [editedEmail, setEditedEmail] = useState(userInfo.email);

  // Perfil label
  const perfilLabel = userInfo.perfil === 'ADMIN_ESCOLA'
    ? (userInfo.cargo === 'DIRETOR' ? 'Diretor' : userInfo.cargo === 'SECRETARIA' ? 'Secretaria' : userInfo.cargo === 'COORDENADOR' ? 'Coordenador' : 'Administrador')
    : userInfo.perfil === 'PROFESSOR' ? 'Professor' : userInfo.perfil;

  return (
    <SovereignSplash titleWord1="Aluno" titleWord2="Conectado" subtitle="Plataforma Acesso a Educacao">
    <div style={SOVEREIGN_BG}>

      {/* Botao sair floating desktop */}
      <button id="ac-logout-desktop" onClick={handleLogout} title="Sair" style={{
        position: 'fixed', top: 20, right: 24, zIndex: 9999,
        background: 'rgba(255,255,255,0.08)', border: '1px solid rgba(255,255,255,0.12)',
        borderRadius: 12, padding: '8px 14px', cursor: 'pointer',
        display: 'flex', alignItems: 'center', gap: 7,
        color: 'rgba(255,255,255,0.65)', fontSize: '.8rem', fontWeight: 600,
        backdropFilter: 'blur(12px)', transition: 'all .2s',
      }}
        onMouseOver={e => { e.currentTarget.style.background = 'rgba(255,255,255,0.15)'; e.currentTarget.style.color = '#fff'; }}
        onMouseOut={e => { e.currentTarget.style.background = 'rgba(255,255,255,0.08)'; e.currentTarget.style.color = 'rgba(255,255,255,0.65)'; }}
      >
        <LogOut size={14} /> Sair
      </button>

      <div style={{
        ...SOVEREIGN_CARD,
        animation: 'bounce-in 0.8s cubic-bezier(0.34, 1.56, 0.64, 1)',
      }}>



        {/* Card de localiza\u00e7\u00e3o da escola */}
        {userInfo.escola?.nome && (
          <div style={{
            display: 'flex', alignItems: 'center', gap: 14,
            padding: '12px 16px', borderRadius: 14,
            background: 'rgba(255,255,255,0.04)',
            border: '1px solid rgba(255,255,255,0.07)',
            marginBottom: 14,
            animation: 'item-bounce 0.6s cubic-bezier(0.34, 1.56, 0.64, 1) 0.25s both',
          }}>
            <div style={{
              width: 38, height: 38, borderRadius: 10, flexShrink: 0,
              background: 'linear-gradient(135deg, rgba(167,139,250,0.18), rgba(124,58,237,0.12))',
              border: '1px solid rgba(167,139,250,0.18)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}>
              <MapPin size={16} style={{ color: '#a78bfa' }} />
            </div>
            <div style={{ flex: 1, minWidth: 0, textAlign: 'left' }}>
              <p style={{ margin: 0, fontSize: '.68rem', color: 'rgba(255,255,255,0.38)', fontWeight: 600, letterSpacing: '.9px', textTransform: 'uppercase' }}>
                Escola
              </p>
              <p style={{ margin: '2px 0 0', fontSize: '.86rem', fontWeight: 700, color: '#fff', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', textTransform: 'uppercase', letterSpacing: '.25px' }}>
                {userInfo.escola.nome}
              </p>
            </div>
          </div>
        )}


        {/* ──── Card do Usuário: Avatar + Nome + Email editáveis ──── */}
        <div style={{
          padding: '16px 18px', borderRadius: 16,
          background: 'rgba(255,255,255,0.05)',
          border: '1px solid rgba(255,255,255,0.08)', marginBottom: 20,
          animation: 'item-bounce 0.6s cubic-bezier(0.34, 1.56, 0.64, 1) 0.3s both',
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
            {/* Avatar */}
            <div style={{ flexShrink: 0 }}>
              {googlePhoto ? (
                <img src={googlePhoto} alt="" style={{
                  width: 56, height: 56, borderRadius: '50%',
                  border: '2.5px solid rgba(167,139,250,0.3)', boxShadow: '0 4px 12px rgba(124,58,237,0.15)',
                  objectFit: 'cover' as any,
                }} />
              ) : (
                <div style={{
                  width: 56, height: 56, borderRadius: '50%',
                  background: 'linear-gradient(135deg, #7c3aed, #6d28d9)',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  color: '#fff', fontSize: '1.15rem', fontWeight: 800, letterSpacing: '.5px',
                  boxShadow: '0 4px 16px rgba(124,58,237,0.3)',
                }}>
                  {getInitials(editedName || userInfo.nome)}
                </div>
              )}
            </div>

            {/* Nome + Email editáveis */}
            <div style={{ flex: 1, minWidth: 0, textAlign: 'left' }}>
              {/* Nome */}
              {editingName ? (
                <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 4 }}>
                  <input
                    type="text" value={editedName}
                    onChange={e => setEditedName(e.target.value)}
                    autoFocus
                    style={{
                      flex: 1, padding: '5px 10px', borderRadius: 8,
                      border: '1.5px solid rgba(167,139,250,0.4)', background: 'rgba(255,255,255,0.08)',
                      fontSize: '.88rem', fontWeight: 700, color: '#ffffff', outline: 'none', minWidth: 0,
                    }}
                    onKeyDown={e => { if (e.key === 'Enter') handleSaveName(); if (e.key === 'Escape') { setEditedName(userInfo.nome); setEditingName(false); } }}
                  />
                  <button onClick={handleSaveName} disabled={savingName} style={{
                    width: 26, height: 26, borderRadius: 7, border: 'none',
                    background: '#6c5ce7', color: '#fff', cursor: 'pointer',
                    display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
                  }}>
                    {savingName ? <Loader2 size={11} className="animate-spin" /> : <Check size={13} />}
                  </button>
                </div>
              ) : (
                <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 4 }}>
                  <p style={{ margin: 0, fontSize: '.95rem', fontWeight: 800, color: '#ffffff', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                    {editedName || userInfo.nome}
                  </p>
                </div>
              )}

              {/* Email */}
              {editingEmail ? (
                <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                  <input
                    type="email" value={editedEmail}
                    onChange={e => setEditedEmail(e.target.value)}
                    autoFocus
                    style={{
                      flex: 1, padding: '4px 8px', borderRadius: 6,
                      border: '1px solid rgba(167,139,250,0.3)', background: 'rgba(255,255,255,0.06)',
                      fontSize: '.78rem', fontWeight: 500, color: 'rgba(255,255,255,0.7)', outline: 'none', minWidth: 0,
                    }}
                    onKeyDown={e => { if (e.key === 'Enter' || e.key === 'Escape') setEditingEmail(false); }}
                  />
                </div>
              ) : (
                <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                  <p style={{ margin: 0, fontSize: '.78rem', color: 'rgba(255,255,255,0.85)', fontWeight: 500, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                    {editedEmail || userInfo.email}
                  </p>
                </div>
              )}

            </div>
          </div>
        </div>

        {error && (
          <div style={{
            padding: '10px 14px', borderRadius: 10, marginBottom: 16,
            background: 'rgba(239,68,68,0.12)', border: '1px solid rgba(239,68,68,0.25)', color: '#fca5a5', fontSize: '.82rem',
            animation: 'item-bounce 0.4s ease both',
          }}>
            {error}
          </div>
        )}

        {/* ──── Step: Choose ──── */}
        {directStep === 'choose' && (
          <div style={{ animation: 'item-bounce 0.6s cubic-bezier(0.34, 1.56, 0.64, 1) 0.4s both' }}>
            {!emailMode ? (
              <>
                <p style={{ fontSize: '.88rem', color: 'rgba(255,255,255,0.55)', textAlign: 'center', margin: '0 0 14px', fontWeight: 500 }}>
                  Confirme sua identidade para ativar:
                </p>
                <div style={{ display: 'flex', gap: 10 }}>
                  <button onClick={handleGoogle} disabled={loading} style={{
                    ...btn, background: 'rgba(255,255,255,0.08)', color: '#ffffff', border: '1.5px solid rgba(255,255,255,0.12)',
                  }}
                    onMouseOver={e => { e.currentTarget.style.background = 'rgba(255,255,255,0.14)'; }}
                    onMouseOut={e => { e.currentTarget.style.background = 'rgba(255,255,255,0.08)'; }}
                  >
                    <svg width="16" height="16" viewBox="0 0 24 24">
                      <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                      <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                      <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l3.66-2.84z"/>
                      <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                    </svg>
                    {loading ? '...' : 'Google'}
                  </button>
                  <button onClick={() => setEmailMode(true)} disabled={loading} style={{
                    ...btn, background: 'rgba(255,255,255,0.04)', color: 'rgba(255,255,255,0.7)', border: '1.5px solid rgba(255,255,255,0.08)',
                  }}
                    onMouseOver={e => { e.currentTarget.style.background = 'rgba(255,255,255,0.1)'; }}
                    onMouseOut={e => { e.currentTarget.style.background = 'rgba(255,255,255,0.04)'; }}
                  >
                    <Mail size={16} />
                    E-mail
                  </button>
                </div>
                <p style={{ fontSize: '.73rem', color: 'rgba(255,255,255,0.25)', margin: '10px 0 0', textAlign: 'center' }}>
                  Recomendado: Google + chave de seguranca
                </p>
              </>
            ) : (
              <div style={{ animation: 'slide-in-right 0.4s cubic-bezier(0.16, 1, 0.3, 1)' }}>
                <p style={{ fontSize: '.88rem', color: 'rgba(255,255,255,0.55)', textAlign: 'center', margin: '0 0 14px', fontWeight: 500 }}>
                  Enviaremos um codigo para seu e-mail:
                </p>
                <div style={{
                  display: 'flex', alignItems: 'center', gap: 0,
                  borderRadius: 14, overflow: 'hidden',
                  border: '1.5px solid rgba(167,139,250,0.25)', background: 'rgba(255,255,255,0.04)',
                }}>
                  <div style={{ padding: '0 14px', display: 'flex', alignItems: 'center' }}>
                    <Mail size={16} style={{ color: 'rgba(255,255,255,0.3)' }} />
                  </div>
                  <input
                    type="email" value={editedEmail || userInfo.email} readOnly
                    style={{
                      flex: 1, padding: '14px 0', border: 'none', background: 'transparent',
                      color: '#ffffff', fontSize: '.9rem', fontWeight: 600, outline: 'none',
                    }}
                  />
                  <button onClick={handleSendCode} disabled={loading} style={{
                    padding: '14px 20px', border: 'none',
                    background: 'linear-gradient(135deg, #6c5ce7, #8b5cf6)',
                    color: '#fff', cursor: 'pointer',
                    display: 'flex', alignItems: 'center', justifyContent: 'center', transition: 'all .2s',
                  }}>
                    {loading ? <Loader2 size={18} className="animate-spin" /> : <KeyRound size={18} />}
                  </button>
                </div>
                <button onClick={() => setEmailMode(false)} style={{
                  background: 'none', border: 'none', color: 'rgba(255,255,255,0.35)', fontSize: '.78rem',
                  cursor: 'pointer', marginTop: 10, fontWeight: 500,
                }}>
                  &#8592; Voltar
                </button>
              </div>
            )}
          </div>
        )}

        {/* ──── Email Code ──── */}
        {directStep === 'email-code' && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 16, animation: 'slide-in-right 0.5s cubic-bezier(0.16, 1, 0.3, 1)' }}>
            <div>
              <label style={{ display: 'block', fontSize: '.85rem', fontWeight: 700, color: 'rgba(255,255,255,0.7)', marginBottom: 10 }}>
                Codigo de verificacao
              </label>
              <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
                <input
                  type="text" inputMode="numeric" maxLength={6} value={code}
                  onChange={e => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                  placeholder="000000" autoFocus
                  style={{
                    flex: 1, padding: '14px 16px', borderRadius: 14, border: '1.5px solid rgba(167,139,250,0.4)',
                    fontSize: '1.4rem', fontWeight: 700, letterSpacing: 8, textAlign: 'center',
                    background: '#ffffff', color: '#0a0a1a', outline: 'none', fontFamily: 'monospace',
                  }}
                />
                <button onClick={handleVerifyCode} disabled={loading || code.length !== 6}
                  style={{ ...btn, flex: 'none', width: 'auto', minWidth: 120, padding: '14px 18px',
                    background: code.length === 6 ? 'linear-gradient(135deg, #6c5ce7, #8b5cf6)' : 'rgba(255,255,255,0.06)',
                    color: code.length === 6 ? '#fff' : 'rgba(255,255,255,0.3)',
                    borderRadius: 14,
                  }}>
                  {loading ? <Loader2 size={18} className="animate-spin" /> : <KeyRound size={18} />}
                  {loading ? '..' : 'Verificar'}
                </button>
              </div>
            </div>
            <button onClick={handleSendCode} disabled={loading}
              style={{ background: 'none', border: 'none', color: '#a78bfa', fontSize: '.88rem', cursor: 'pointer', fontWeight: 600 }}>
              Reenviar codigo
            </button>
          </div>
        )}

        {/* ──── Step: Set Password ──── */}
        {directStep === 'set-password' && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 14, animation: 'slide-in-right 0.5s cubic-bezier(0.16, 1, 0.3, 1)' }}>
            <p style={{ margin: 0, fontSize: '.82rem', color: 'rgba(255,255,255,0.45)', textAlign: 'center', fontWeight: 500 }}>
              Defina sua senha para acesso seguro
            </p>
            <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
              <input type="password" value={newPassword} onChange={e => setNewPassword(e.target.value)} placeholder="Nova senha"
                style={{ flex: 1, padding: '13px 14px', borderRadius: 12, border: '1.5px solid rgba(167,139,250,0.3)',
                  background: '#ffffff', color: '#0a0a1a', outline: 'none', fontSize: '.9rem', fontWeight: 600 }} />
              <input type="password" value={confirmPassword} onChange={e => setConfirmPassword(e.target.value)} placeholder="Confirmar"
                style={{ flex: 1, padding: '13px 14px', borderRadius: 12, border: '1.5px solid rgba(167,139,250,0.3)',
                  background: '#ffffff', color: '#0a0a1a', outline: 'none', fontSize: '.9rem', fontWeight: 600 }} />
              <button onClick={handleSetPassword} disabled={loading || newPassword.length < 8 || confirmPassword.length < 8}
                style={{ ...btn, flex: 'none', width: 'auto', minWidth: 110, padding: '13px 18px',
                  background: newPassword.length >= 8 && confirmPassword.length >= 8 ? 'linear-gradient(135deg, #6c5ce7, #8b5cf6)' : 'rgba(255,255,255,0.06)',
                  color: newPassword.length >= 8 && confirmPassword.length >= 8 ? '#fff' : 'rgba(255,255,255,0.3)',
                  borderRadius: 12,
                }}>
                {loading ? <Loader2 size={18} className="animate-spin" /> : <KeyRound size={18} />}
                {loading ? '..' : 'Salvar'}
              </button>
            </div>
          </div>
        )}

        {/* ──── Step: Passkey ──── */}
        {directStep === 'passkey' && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 16, animation: 'slide-in-right 0.5s cubic-bezier(0.16, 1, 0.3, 1)' }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 12, padding: '16px 20px', borderRadius: 14, background: 'rgba(34,197,94,0.08)', border: '1px solid rgba(34,197,94,0.2)' }}>
              <CheckCircle2 size={22} color="#4ade80" style={{ flexShrink: 0 }} />
              <p style={{ margin: 0, fontSize: '.95rem', color: '#86efac', fontWeight: 700 }}>Identidade verificada!</p>
            </div>
            <p style={{ fontSize: '.95rem', color: 'rgba(255,255,255,0.6)', textAlign: 'center', margin: 0, lineHeight: 1.5 }}>
              Cadastre sua chave de seguranca:
            </p>
            <button onClick={handlePasskey} disabled={loading}
              style={{ ...btn, flex: 'none', width: '100%', background: 'linear-gradient(135deg, #6c5ce7, #a29bfe)', color: '#fff', boxShadow: '0 4px 14px rgba(108,92,231,0.4)' }}>
              {loading ? <Loader2 size={18} className="animate-spin" /> : <Fingerprint size={20} />}
              {loading ? 'Registrando...' : 'Cadastrar Chave de Seguranca'}
            </button>
          </div>
        )}

        {/* ──── Step: Done ──── */}
        {directStep === 'done' && (
          <div style={{ textAlign: 'center', animation: 'bounce-in 0.6s cubic-bezier(0.34, 1.56, 0.64, 1)' }}>
            <div style={{ width: 64, height: 64, borderRadius: '50%', margin: '0 auto 16px', background: 'linear-gradient(135deg, #16a34a, #4ade80)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <CheckCircle2 size={32} color="#fff" />
            </div>
            <p style={{ fontSize: '1.15rem', color: '#4ade80', fontWeight: 800, margin: '0 0 10px' }}>Conta ativada com sucesso!</p>
            <p style={{ fontSize: '.95rem', color: 'rgba(255,255,255,0.5)', margin: 0 }}>Redirecionando para o painel...</p>
            <div style={{ width: 36, height: 36, border: '3px solid rgba(255,255,255,0.1)', borderTopColor: '#a78bfa', borderRadius: '50%', animation: 'spin .8s linear infinite', margin: '24px auto 0' }} />
          </div>
        )}

        <style>{`
          @keyframes spin { to { transform: rotate(360deg) } }
          @keyframes bounce-in {
            0% { opacity: 0; transform: scale(0.85) translateY(30px); }
            60% { opacity: 1; transform: scale(1.03) translateY(-4px); }
            100% { transform: scale(1) translateY(0); }
          }
          @keyframes item-bounce {
            0% { opacity: 0; transform: translateY(20px) scale(0.95); }
            60% { opacity: 1; transform: translateY(-3px) scale(1.01); }
            100% { transform: translateY(0) scale(1); }
          }
          @keyframes slide-in-right {
            from { opacity: 0; transform: translateX(30px); }
            to { opacity: 1; transform: translateX(0); }
          }
          @keyframes pan-bg { 0% { background-position: 0% 50%; } 50% { background-position: 100% 50%; } 100% { background-position: 0% 50%; } }
        `}</style>
      </div>
    </div>
    </SovereignSplash>
  );
}
